import type { ErrorLike } from '@apollo/client/core';
import { CombinedGraphQLErrors } from '@apollo/client/errors';

export interface GraphqlFailure {
  readonly message?: string;
  readonly extensions?: Readonly<Record<string, unknown>>;
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null;
}

function isGraphqlFailure(value: unknown): value is GraphqlFailure {
  return isRecord(value) &&
    (value.message === undefined || typeof value.message === 'string') &&
    (value.extensions === undefined || isRecord(value.extensions));
}

/** Read transport errors and application-owned GraphQL error envelopes at one boundary. */
export function getApolloGraphqlErrors(error: unknown): readonly GraphqlFailure[] {
  if (CombinedGraphQLErrors.is(error)) return error.errors;
  const errors = Array.isArray(error)
    ? error
    : isRecord(error) ? error.graphQLErrors ?? error.errors : undefined;
  return Array.isArray(errors) ? errors.filter(isGraphqlFailure) : [];
}

export function getApolloErrorCode(error: unknown): string | null {
  const code = getApolloGraphqlErrors(error)[0]?.extensions?.code;
  return typeof code === 'string' ? code : null;
}

/** Apollo 4 passes GraphQL aggregates or the original network error, without ApolloError. */
export function apolloErrorDetails(error: ErrorLike) {
  return CombinedGraphQLErrors.is(error)
    ? { graphQLErrors: error.errors, networkError: undefined }
    : { graphQLErrors: undefined, networkError: error };
}
